import type { DataSource, QueryInputsType } from '@unifyapps/defs/types/dataSource';
import type { DependsOnType, DynamicPath } from '@unifyapps/defs/types/page';
import { memo, useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { useDSCActions, useDSCGetState } from '../../context/selectors';
import { useComputeDataSourcePayload } from '../../hooks/useComputeDataSourcePayload';
import { useExecuteDataSourceCallbacks } from '../../hooks/useExecuteDataSourceCallbacks';
import type { QueryNode } from '../../types';
import { DataSourcePulseObserverGate } from '../DataSourcePulseObserver/DataSourcePulseObserverGate';
import useUAQuerySource from './useUAQuerySource';

function UAQueryNode({
  id,
  query,
  onQueryCompletion,
  callbacks,
  requestId,
  dependsOn,
  dynamicPaths,
}: {
  id: string;
  query: QueryInputsType;
  callbacks: DataSource['callbacks'];
  onQueryCompletion: QueryNode['onQueryCompletion'];
  requestId: string;
  dependsOn?: DependsOnType[];
  dynamicPaths?: DynamicPath[];
}) {
  const computedPayload = useComputeDataSourcePayload<{
    id: string;
    inputs: QueryInputsType;
  }>({
    data: { id, inputs: query },
    dataSourceId: id,
    key: requestId,
    dependsOn,
    dynamicPaths,
  });
  const { setDataSourceState } = useDSCActions();
  const getDataSourceControllerState = useDSCGetState();

  const queryResult = useUAQuerySource({ query: computedPayload.inputs.payload });

  useExecuteDataSourceCallbacks({
    callbacks,
    onQueryCompletion,
    id,
    requestId,
    queryResult: {
      error: queryResult.error,
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
    },
  });

  useEffect(() => {
    const previousDataSource = getDataSourceControllerState().dataSources[id];
    if (isEqual(queryResult, previousDataSource)) return;
    setDataSourceState(id, queryResult);
  }, [getDataSourceControllerState, id, queryResult, setDataSourceState]);

  return (
    <DataSourcePulseObserverGate
      dsId={id}
      enabled
      error={queryResult.error}
      isFetching={Boolean(queryResult.isFetching)}
      objects={queryResult.objects}
      requestPayload={computedPayload.inputs.payload}
    />
  );
}

export default memo(UAQueryNode);
